/**
 * ScrollbarOverlay — the design-system scrollbar drawn as real DOM, overlaid
 * on a natively-scrolling element whose own scrollbar is hidden.
 *
 *   ┌──┬───┐
 *   │  │ ▓ │   lane:     12px wide, panel-coloured
 *   │  │ ▓ │   hairline: 0.5px dinnerplate-white rule on the lane's left edge
 *   │  │ ▓ │   thumb:     6px dinnerplate-white pill, flush to the lane's right
 *   └──┴───┘
 *
 * Why DOM instead of `::-webkit-scrollbar` (see `./scrollbar.ts`): at a 12px
 * lane the native version has to inset the thumb with a transparent border, and
 * a border-box `border-radius` then rounds only the edges without an adjacent
 * border — so the pill renders square on one side. Native scrollbar parts also
 * render differently per platform (and not at all under overlay scrollbars),
 * which makes them impossible to verify. Real elements render identically
 * everywhere and can be measured in tests.
 *
 * Usage — the target keeps native scrolling (so keyboard nav and
 * scroll-into-view still work), just without a visible native bar:
 *
 *   <div className="relative">
 *     <div ref={ref} className={cn('overflow-y-auto', HIDE_NATIVE_SCROLLBAR)}>…</div>
 *     <ScrollbarOverlay targetRef={ref} />
 *   </div>
 */

// React
import { useCallback, useEffect, useRef, useState } from 'react';

// Hooks and utilities
import { cn } from '@/lib/utils';

/** Hide a container's native scrollbar while keeping it scrollable. */
export const HIDE_NATIVE_SCROLLBAR =
  '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&::-webkit-scrollbar]:size-0';

const LANE_WIDTH = 12;
const THUMB_WIDTH = 6;
/**
 * Vertical breathing room at each end of the lane, so the thumb never touches
 * the search bar above or the footer separator below (Figma draws the thumb at
 * y=6 within the lane).
 */
const LANE_PADDING = 6;
/** Keeps a short thumb grabbable on very long lists. */
const MIN_THUMB_HEIGHT = 40;

interface ScrollbarOverlayProps {
  /** The natively-scrolling element this scrollbar represents. */
  targetRef: React.RefObject<HTMLElement | null>;
  /** Lane background — match the panel so the lane reads as empty space. */
  laneClassName?: string;
  /** Inset (px) from the top of the positioning parent, e.g. a sticky header. */
  top?: number;
}

export function ScrollbarOverlay({
  targetRef,
  laneClassName = 'bg-off-white',
  top = 0,
}: ScrollbarOverlayProps) {
  const [thumb, setThumb] = useState({ top: 0, height: 0, visible: false });
  const dragRef = useRef<{ startY: number; startScrollTop: number; ratio: number } | null>(
    null
  );

  const recalculate = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    // +1 absorbs sub-pixel rounding, which would otherwise show a thumb on
    // content that doesn't actually overflow.
    if (scrollHeight <= clientHeight + 1) {
      setThumb({ top: 0, height: 0, visible: false });
      return;
    }

    // The thumb travels inside the padded part of the lane, so both ends keep
    // their 6px inset at the extremes of the scroll.
    const track = Math.max(0, clientHeight - 2 * LANE_PADDING);
    const height = Math.min(track, Math.max(MIN_THUMB_HEIGHT, (clientHeight / scrollHeight) * track));
    const scrollable = scrollHeight - clientHeight;
    const travel = track - height;
    setThumb({
      top: LANE_PADDING + (scrollable > 0 ? (scrollTop / scrollable) * travel : 0),
      height,
      visible: true,
    });
  }, [targetRef]);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    recalculate();
    el.addEventListener('scroll', recalculate);
    // Re-measure when the list is filtered or the panel is resized.
    const observer = new ResizeObserver(recalculate);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', recalculate);
      observer.disconnect();
    };
  }, [targetRef, recalculate]);

  // Drag handling lives on window so the pointer can leave the 6px thumb
  // mid-drag without dropping it.
  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const el = targetRef.current;
      const drag = dragRef.current;
      if (!el || !drag) return;
      const scrollable = el.scrollHeight - el.clientHeight;
      el.scrollTop = Math.min(
        scrollable,
        Math.max(0, drag.startScrollTop + (event.clientY - drag.startY) * drag.ratio)
      );
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [targetRef]);

  const onThumbPointerDown = (event: React.PointerEvent) => {
    event.preventDefault();
    const el = targetRef.current;
    if (!el) return;
    // Same padded track as recalculate(), so a drag maps 1:1 to the thumb's
    // rendered position instead of drifting.
    const travel = el.clientHeight - 2 * LANE_PADDING - thumb.height;
    if (travel <= 0) return;
    dragRef.current = {
      startY: event.clientY,
      startScrollTop: el.scrollTop,
      ratio: (el.scrollHeight - el.clientHeight) / travel,
    };
  };

  if (!thumb.visible) return null;

  return (
    <div
      data-testid="scrollbar-lane"
      aria-hidden="true"
      style={{ top, width: LANE_WIDTH }}
      className={cn(
        'absolute right-0 bottom-0 z-20 pointer-events-none',
        'border-l-[0.5px] border-dinnerplate-white',
        laneClassName
      )}
    >
      <div
        data-testid="scrollbar-thumb"
        onPointerDown={onThumbPointerDown}
        style={{ top: thumb.top, height: thumb.height, width: THUMB_WIDTH }}
        className={cn(
          'absolute right-0 rounded-full bg-dinnerplate-white',
          'pointer-events-auto cursor-grab active:cursor-grabbing select-none',
          'hover:bg-grey-light transition-colors duration-100 ease-out'
        )}
      />
    </div>
  );
}
